import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { FREE_MODELS, normalizeModelId, fetchModelCatalog } from "../lib/aiModels";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Save, Sparkles, Code, Copy, Check, Eye, RefreshCw } from "lucide-react";

// Self-contained document for the live widget preview iframe. The embed
// snippet from GET /assistants/{id}/widget-code uses a relative /widget.js,
// which cannot resolve inside a sandboxed srcDoc frame — so we point both the
// loader script and the widget's API base at the backend origin explicitly.
function buildWidgetPreviewDoc(slug) {
  const backendOrigin = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, "");
  return `<!DOCTYPE html>
<html>
<head><meta charset="utf-8" /><style>
  html,body{height:100%;margin:0}
  body{background:repeating-linear-gradient(45deg,#f8fafc,#f8fafc 12px,#f1f5f9 12px,#f1f5f9 24px);font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif}
  .hint{position:absolute;top:12px;left:12px;right:12px;color:#94a3b8;font-size:11px;text-align:center}
</style></head>
<body>
  <p class="hint">Live preview — click the chat bubble to try your widget</p>
  <script>window.APP_MI_WIDGET = { slug: ${JSON.stringify(slug)}, server: ${JSON.stringify(backendOrigin)} };</script>
  <script src="${backendOrigin}/widget.js" defer></script>
</body>
</html>`;
}

const PURPOSE_CHOICES = ["best_friend", "accountant", "personal_shopper", "therapist", "tutor", "fitness_coach", "legal_advisor", "travel_planner", "writing_partner", "tech_support", "custom"];
const TONE_CHOICES = ["friendly", "professional", "casual", "direct"];
const VOICE_CHOICES = ["male", "female", "neutral"];
const TIER_CHOICES = ["basic", "standard", "premium", "enterprise"];

export default function EditAssistantPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assistant, setAssistant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({});
  const [showTierDialog, setShowTierDialog] = useState(false);
  const [pendingTier, setPendingTier] = useState(null);
  const [widgetEnabled, setWidgetEnabled] = useState(false);
  const [widgetTheme, setWidgetTheme] = useState({ primary_color: "#4F46E5", position: "bottom-right", greeting_message: "", placeholder_text: "" });
  const [widgetSaving, setWidgetSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [models, setModels] = useState(FREE_MODELS);
  const [widgetCode, setWidgetCode] = useState(null); // {enabled, slug, embed_code}
  const [previewKey, setPreviewKey] = useState(0); // bump to reload the preview iframe

  useEffect(() => {
    if (!widgetEnabled) { setWidgetCode(null); return; }
    api.get(`/assistants/${id}/widget-code`)
      .then((r) => setWidgetCode(r.data))
      .catch(() => setWidgetCode(null));
  }, [id, widgetEnabled]);

  useEffect(() => {
    // Free-model catalog (falls back to the static list on error).
    fetchModelCatalog(api).then((catalog) => setModels(catalog.models));
  }, []);

  useEffect(() => {
    api.get(`/assistants`).then((r) => {
      const found = r.data.find((a) => a.id === id);
      if (found) {
        setAssistant(found);
        setForm({ name: found.name, purpose: found.purpose, personality_tone: found.personality_tone, voice_gender: found.voice_gender, ai_model: found.ai_model, subscription_tier: found.subscription_tier, custom_instructions: found.custom_instructions || "", is_active: found.is_active });
        setWidgetEnabled(found.widget_enabled || false);
        if (found.widget_theme) setWidgetTheme(prev => ({ ...prev, ...found.widget_theme }));
      } else {
        setError("Assistant not found");
      }
    }).catch(() => setError("Failed to load assistant")).finally(() => setLoading(false));
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await api.put(`/assistants/${id}`, form);
      toast.success("Changes saved successfully!");
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to save";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleTierChange = (tier) => {
    const tierRank = { basic: 0, standard: 1, premium: 2, enterprise: 3 };
    if (tierRank[tier] < tierRank[form.subscription_tier]) {
      setPendingTier(tier);
      setShowTierDialog(true);
    } else {
      setForm({ ...form, subscription_tier: tier });
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Sparkles className="w-6 h-6 text-cyan-400 animate-pulse" />
    </div>
  );

  if (error && !assistant) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center animate-fade-in-up">
        <p className="text-red-500 mb-4">{error}</p>
        <Button onClick={() => navigate("/dashboard")} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white press-down">Back to Dashboard</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" data-testid="edit-assistant-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground/80 mb-6 bg-transparent border-0 cursor-pointer transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </button>

        <div className="flex items-center justify-between mb-8 animate-fade-in-up">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-semibold mb-1">Edit Assistant</p>
            <h1 className="font-outfit text-2xl font-medium text-foreground" data-testid="edit-heading">{assistant?.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">/{assistant?.slug}</p>
          </div>
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-sm text-red-400 mb-6 animate-fade-in" data-testid="edit-error">{error}</div>}

        <div className="space-y-6">
          {/* Name */}
          <div className="bg-card border border-border/60 rounded-xl p-6 card-hover">
            <label className="block text-sm font-medium text-foreground/80 mb-2">Assistant Name</label>
            <input data-testid="edit-name-input" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-2.5 bg-muted/40 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400 transition-colors" />
          </div>

          {/* Purpose */}
          <div className="bg-card border border-border/60 rounded-xl p-6 card-hover">
            <label className="block text-sm font-medium text-foreground/80 mb-3">Purpose</label>
            <div className="flex flex-wrap gap-2">
              {PURPOSE_CHOICES.map((p) => (
                <button key={p} onClick={() => setForm({ ...form, purpose: p })} className={`px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer transition-all hover:-translate-y-0.5 ${form.purpose === p ? "border-cyan-400 bg-cyan-500/10 text-cyan-300 ring-1 ring-cyan-400/30" : "border-border bg-card text-muted-foreground hover:border-border"}`} data-testid={`edit-purpose-${p}`}>
                  {p.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </div>

          {/* Tone & Voice */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-card border border-border/60 rounded-xl p-6 card-hover">
              <label className="block text-sm font-medium text-foreground/80 mb-3">Personality Tone</label>
              <div className="flex flex-wrap gap-2">
                {TONE_CHOICES.map((t) => (
                  <button key={t} onClick={() => setForm({ ...form, personality_tone: t })} className={`px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer transition-all hover:-translate-y-0.5 ${form.personality_tone === t ? "border-cyan-400 bg-cyan-500/10 text-cyan-300 ring-1 ring-cyan-400/30" : "border-border bg-card text-muted-foreground hover:border-border"}`} data-testid={`edit-tone-${t}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="bg-card border border-border/60 rounded-xl p-6 card-hover">
              <label className="block text-sm font-medium text-foreground/80 mb-3">Voice Gender</label>
              <div className="flex flex-wrap gap-2">
                {VOICE_CHOICES.map((v) => (
                  <button key={v} onClick={() => setForm({ ...form, voice_gender: v })} className={`px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer transition-all hover:-translate-y-0.5 ${form.voice_gender === v ? "border-cyan-400 bg-cyan-500/10 text-cyan-300 ring-1 ring-cyan-400/30" : "border-border bg-card text-muted-foreground hover:border-border"}`} data-testid={`edit-voice-${v}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Model */}
          <div className="bg-card border border-border/60 rounded-xl p-6 card-hover">
            <label className="block text-sm font-medium text-foreground/80 mb-1">AI Model</label>
            <p className="text-xs text-muted-foreground/70 mb-3">All 8 AI models are free and available on every plan.</p>
            <div className="flex flex-wrap gap-2">
              {models.map((m) => {
                const selected = normalizeModelId(form.ai_model) === m.id;
                return (
                  <button key={m.id} onClick={() => setForm({ ...form, ai_model: m.id })} className={`px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer transition-all hover:-translate-y-0.5 ${selected ? "border-cyan-400 bg-cyan-500/10 text-cyan-300 ring-1 ring-cyan-400/30" : "border-border bg-card text-muted-foreground hover:border-border"}`} data-testid={`edit-model-${m.id}`} title={`${m.provider} — ${m.desc}`}>
                    {m.name}
                    <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 font-semibold">Free</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tier */}
          <div className="bg-card border border-border/60 rounded-xl p-6 card-hover">
            <label className="block text-sm font-medium text-foreground/80 mb-3">Subscription Tier</label>
            <div className="flex flex-wrap gap-2">
              {TIER_CHOICES.map((t) => (
                <button key={t} onClick={() => handleTierChange(t)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer transition-all hover:-translate-y-0.5 ${form.subscription_tier === t ? "border-cyan-400 bg-cyan-500/10 text-cyan-300 ring-1 ring-cyan-400/30" : "border-border bg-card text-muted-foreground hover:border-border"}`} data-testid={`edit-tier-${t}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Instructions */}
          <div className="bg-card border border-border/60 rounded-xl p-6 card-hover">
            <label className="block text-sm font-medium text-foreground/80 mb-2">Custom Instructions</label>
            <textarea data-testid="edit-instructions-input" value={form.custom_instructions || ""} onChange={(e) => setForm({ ...form, custom_instructions: e.target.value })} rows={4} maxLength={2000} className="w-full px-4 py-2.5 bg-muted/40 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400 resize-none transition-colors" placeholder="Add special instructions for your AI..." />
            <p className="text-xs text-muted-foreground/70 mt-1">{(form.custom_instructions || "").length}/2000</p>
          </div>

          {/* Active Toggle */}
          <div className="bg-card border border-border/60 rounded-xl p-6 flex items-center justify-between card-hover">
            <div>
              <p className="text-sm font-medium text-foreground/80">Active Status</p>
              <p className="text-xs text-muted-foreground">Disable to temporarily hide your assistant</p>
            </div>
            <button onClick={() => setForm({ ...form, is_active: !form.is_active })} className={`w-12 h-6 rounded-full transition-all cursor-pointer border-0 ${form.is_active ? "bg-indigo-500" : "bg-muted"}`} data-testid="edit-active-toggle">
              <div className={`w-5 h-5 rounded-full bg-card shadow-sm transition-transform ${form.is_active ? "translate-x-6" : "translate-x-0.5"}`} />
            </button>
          </div>

          {/* Widget Settings */}
          <div className="bg-card border border-border/60 rounded-xl p-6 card-hover" data-testid="widget-settings">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-medium text-foreground/80 flex items-center gap-2"><Code className="w-4 h-4 text-cyan-400" /> Embeddable Widget</p>
                <p className="text-xs text-muted-foreground">Let visitors chat with this AI on any website</p>
              </div>
              <button onClick={async () => {
                setWidgetSaving(true);
                try {
                  const r = await api.put(`/assistants/${id}/widget`, { widget_enabled: !widgetEnabled, widget_theme: widgetTheme });
                  setWidgetEnabled(r.data.widget_enabled);
                  toast.success(r.data.widget_enabled ? "Widget enabled!" : "Widget disabled");
                } catch (err) { toast.error("Failed to update widget"); }
                setWidgetSaving(false);
              }} className={`w-12 h-6 rounded-full transition-all cursor-pointer border-0 ${widgetEnabled ? "bg-indigo-500" : "bg-muted"}`} data-testid="widget-toggle" disabled={widgetSaving}>
                <div className={`w-5 h-5 rounded-full bg-card shadow-sm transition-transform ${widgetEnabled ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
            </div>

            {widgetEnabled && (
              <div className="space-y-4 mt-4 pt-4 border-t border-border/60">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Primary Color</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={widgetTheme.primary_color} onChange={(e) => setWidgetTheme({ ...widgetTheme, primary_color: e.target.value })} className="w-8 h-8 rounded border border-border cursor-pointer" data-testid="widget-color" />
                      <span className="text-xs text-muted-foreground">{widgetTheme.primary_color}</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Position</label>
                    <div className="flex gap-2">
                      {["bottom-right", "bottom-left"].map(pos => (
                        <button key={pos} onClick={() => setWidgetTheme({ ...widgetTheme, position: pos })} className={`px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer transition-all ${widgetTheme.position === pos ? "border-cyan-400 bg-cyan-500/10 text-cyan-300" : "border-border bg-card text-muted-foreground"}`} data-testid={`widget-pos-${pos}`}>
                          {pos.replace("-", " ")}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Greeting Message</label>
                  <input value={widgetTheme.greeting_message} onChange={(e) => setWidgetTheme({ ...widgetTheme, greeting_message: e.target.value })} placeholder={`Hi! I'm ${form.name || "AI"}. How can I help you?`} className="w-full px-3 py-2 bg-muted/40 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-400/25" data-testid="widget-greeting" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Placeholder Text</label>
                  <input value={widgetTheme.placeholder_text} onChange={(e) => setWidgetTheme({ ...widgetTheme, placeholder_text: e.target.value })} placeholder="Type a message..." className="w-full px-3 py-2 bg-muted/40 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-400/25" data-testid="widget-placeholder" />
                </div>
                <button onClick={async () => {
                  setWidgetSaving(true);
                  try {
                    await api.put(`/assistants/${id}/widget`, { widget_enabled: true, widget_theme: widgetTheme });
                    toast.success("Widget theme saved!");
                  } catch { toast.error("Failed to save theme"); }
                  setWidgetSaving(false);
                }} className="text-xs bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg cursor-pointer border-0 transition-colors" data-testid="widget-save-theme" disabled={widgetSaving}>
                  {widgetSaving ? "Saving..." : "Save Theme"}
                </button>

                {/* Embed Code */}
                <div className="mt-4 pt-4 border-t border-border/60">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Embed Code</p>
                  <div className="relative bg-gray-900 rounded-lg p-4 text-xs text-green-400 font-mono overflow-x-auto">
                    <pre className="whitespace-pre-wrap">{`<script>\n  window.APP_MI_WIDGET = {\n    slug: "${assistant?.slug || ""}"\n  };\n</script>\n<script src="${window.location.origin}/widget.js" defer></script>`}</pre>
                    <button onClick={() => {
                      navigator.clipboard.writeText(`<script>\n  window.APP_MI_WIDGET = {\n    slug: "${assistant?.slug || ""}"\n  };\n</script>\n<script src="${window.location.origin}/widget.js" defer></script>`);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }} className="absolute top-2 right-2 bg-white/10 hover:bg-white/20 text-white p-1.5 rounded cursor-pointer border-0 transition-colors" data-testid="widget-copy-code">
                      {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>

                {/* Live Preview */}
                <div className="mt-4 pt-4 border-t border-border/60" data-testid="widget-preview-panel">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                      <Eye className="w-3.5 h-3.5 text-cyan-400" /> Preview
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 font-semibold uppercase tracking-wider">Live</span>
                    </p>
                    <button onClick={() => setPreviewKey((k) => k + 1)} className="flex items-center gap-1 text-[10px] text-muted-foreground/70 hover:text-muted-foreground bg-transparent border-0 cursor-pointer transition-colors" data-testid="widget-preview-refresh" title="Reload preview (e.g. after saving the theme)">
                      <RefreshCw className="w-3 h-3" /> Reload
                    </button>
                  </div>
                  <iframe
                    key={previewKey}
                    title="Widget preview"
                    sandbox="allow-scripts allow-same-origin"
                    srcDoc={buildWidgetPreviewDoc(widgetCode?.slug || assistant?.slug || "")}
                    className="w-full h-[420px] rounded-lg border border-border bg-muted/40"
                    data-testid="widget-preview-iframe"
                  />
                  <p className="text-[10px] text-muted-foreground/70 mt-1.5">This is your real widget served from the API. Save the theme, then hit Reload to see changes. If the bubble doesn't appear, make sure the widget is enabled.</p>
                </div>
              </div>
            )}
          </div>

          {/* Save */}
          <Button data-testid="edit-save-btn" onClick={handleSave} disabled={saving} className="w-full bg-gradient-to-r from-cyan-400 to-violet-500 text-white h-11 rounded-lg font-medium shadow-glow-sm hover:-translate-y-0.5 transition-all press-down">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* Tier Downgrade Dialog */}
      <Dialog open={showTierDialog} onOpenChange={setShowTierDialog}>
        <DialogContent className="bg-card border border-border/60 rounded-2xl text-foreground shadow-xl" data-testid="tier-downgrade-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-foreground">Downgrade Plan?</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Switching to <strong className="text-foreground">{pendingTier}</strong> lowers your message limits and assistant slots. All AI models stay free on every plan. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowTierDialog(false)} className="border-border text-foreground/80 bg-card hover:bg-white/5 press-down">Cancel</Button>
            <Button onClick={() => { setForm({ ...form, subscription_tier: pendingTier }); setShowTierDialog(false); }} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white press-down" data-testid="confirm-downgrade-btn">
              Confirm Downgrade
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
